import { useState, useEffect, useRef, KeyboardEvent } from 'react';

export interface MentionFriend {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar_config?: any;
    plan_type?: string;
}

export interface UseMentionReturn {
    text: string;
    setText: (text: string) => void;
    handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    handleSelectMention: (username: string) => void;
    handleCursorChange: (cursorPos: number) => void;
    mentionResults: MentionFriend[];
    selectedIndex: number;
    showDropdown: boolean;
}

export const useMention = (
    initialText: string,
    friends: MentionFriend[]
): UseMentionReturn => {
    const [text, setText] = useState(initialText);
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);
    const [mentionSearch, setMentionSearch] = useState<string>('');
    const [mentionResults, setMentionResults] = useState<MentionFriend[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Watch text for mentions (@) based on cursor position
    useEffect(() => {
        if (!text || cursorPosition === null) {
            setMentionResults([]);
            return;
        }

        const textBeforeCursor = text.slice(0, cursorPosition);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        if (lastWord.startsWith('@') && lastWord.length > 0) {
            const query = lastWord.substring(1).toLowerCase();
            setMentionSearch(query);
            
            const filtered = friends.filter(friend => 
                friend.username?.toLowerCase().includes(query) || 
                friend.display_name?.toLowerCase().includes(query)
            ).slice(0, 3);
            
            setMentionResults(filtered);
            setSelectedIndex(0); // Reset index on new search
        } else {
            setMentionResults([]);
        }
    }, [text, cursorPosition, friends]);

    const handleSelectMention = (username: string) => {
        if (cursorPosition === null) return;

        const textBeforeCursor = text.slice(0, cursorPosition);
        const textAfterCursor = text.slice(cursorPosition);
        const words = textBeforeCursor.split(/\s/);

        // Remove the typed `@query`
        words.pop();

        const newTextBeforeCursor = words.length > 0 ? words.join(' ') + ' ' : '';
        const mentionText = `@${username} `;
        const newText = newTextBeforeCursor + mentionText + textAfterCursor;

        setText(newText);
        setCursorPosition(newTextBeforeCursor.length + mentionText.length);
        setMentionResults([]);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (mentionResults.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => (prev + 1) % mentionResults.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => (prev - 1 + mentionResults.length) % mentionResults.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleSelectMention(mentionResults[selectedIndex].username);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setMentionResults([]);
            }
        }
    };

    const handleCursorChange = (cursorPos: number) => {
        setCursorPosition(cursorPos);
    };

    return {
        text,
        setText,
        handleKeyDown,
        handleSelectMention,
        handleCursorChange,
        mentionResults,
        selectedIndex,
        showDropdown: mentionResults.length > 0
    };
};
